/**
 * FactorSearchModal — reusable pure factor selector.
 *
 * Pure selector: takes a prefilled query + dimensions, lets the user
 * narrow the search (free-text Query, Source, Region, Year, with
 * Unit Type pinned read-only), and emits the chosen factor via
 * `onSelect`. The caller decides what to do with the selection
 * (apply override on an activity, attach to a draft wizard row, etc.).
 *
 * Reused by:
 *  - FactorAlternativesSection (Activity Detail page / drawer)
 *  - The add-activity wizard (planned, see GU-7017 follow-up)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'reactstrap';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import type { AlternativeFactor, EmissionScope } from '../../types/emissions';
import {
  useFactorSearchPaginatedMutation,
  type FactorSearchPagination,
  type FactorSearchQuery,
} from '../../api/emissions';

export interface FactorSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiativeId: string;
  /** Activity scope (passed to the search; not editable in the UI). */
  scope: EmissionScope;
  /** Activity type (passed to the search; not editable in the UI). */
  activityType: string;
  /**
   * Climatiq unit_type pinned for the search (e.g. ['Energy']). Shown
   * read-only in the modal and forwarded to the search so the user
   * cannot pick a factor with an incompatible unit family.
   */
  unitType?: string[];
  /** Initial free-text query the modal opens with. */
  initialQuery?: string;
  /** Initial Region (clearable to "any"). */
  initialRegion?: string;
  /** Initial Year (clearable to "any"). */
  initialYear?: number;
  /** Initial Source filter (defaults to "any"). */
  initialSource?: string;
  /**
   * Currently-applied factor id, if any. The list shows it as "current"
   * and disables selection — picking it again would be a no-op.
   */
  currentFactorId?: string;
  /**
   * Optional regions list for the Region dropdown. If omitted the modal
   * renders a free-text input. Pass REGION_OPTIONS from form/types to
   * get the wizard's full picker.
   */
  regions?: { value: string; label: string }[];
  /**
   * Called when the user clicks "Apply" with a selected factor. The
   * caller is responsible for actually applying it (override mutation,
   * staging on a draft row, etc.). Should throw to signal failure;
   * the modal will surface the error and stay open.
   */
  onSelect: (factor: AlternativeFactor) => Promise<void>;
}

// Sources we expose in the modal dropdown. `premium` flags the ones
// Climatiq licenses behind a paid tier — picking a premium source on a
// free plan produces a 4xx from /search, which we surface inline.
// Public/free sources first, then premium clearly marked.
const SOURCE_OPTIONS: { value: string; label: string; premium?: boolean }[] = [
  { value: 'DEFRA', label: 'DEFRA (UK)' },
  { value: 'BEIS', label: 'BEIS (UK)' },
  { value: 'EPA', label: 'EPA (US)' },
  { value: 'ADEME', label: 'ADEME (FR)' },
  { value: 'SEFR', label: 'SEFR (Singapore)' },
  { value: 'IPCC', label: 'IPCC' },
  { value: 'GHG Protocol', label: 'GHG Protocol' },
  { value: 'IEA', label: 'IEA', premium: true },
  { value: 'EXIOBASE', label: 'EXIOBASE', premium: true },
  { value: 'ecoinvent', label: 'ecoinvent', premium: true },
];

const DEFAULT_RESULTS_PER_PAGE = 20;

const formatSourceLabel = (value: string): string => {
  const match = SOURCE_OPTIONS.find((s) => s.value.toLowerCase() === value.toLowerCase());
  return match ? match.label.replace(/ — premium$/, '') : value;
};

const formatRegionLabel = (
  value: string,
  regions?: { value: string; label: string }[],
): string => {
  if (!regions || regions.length === 0) return value;
  const match = regions.find((r) => r.value === value);
  return match ? match.label : value;
};

const formatFactorValue = (value: number | null): string => {
  if (value === null || value === undefined) return '--';
  if (value === 0) return '0';
  if (!Number.isFinite(value)) return String(value);
  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  const decimals = Math.max(2, 2 - magnitude + 2);
  return value.toFixed(Math.min(decimals, 10));
};

export const FactorSearchModal = ({
  isOpen,
  onClose,
  initiativeId,
  scope,
  activityType,
  unitType,
  initialQuery = '',
  initialRegion,
  initialYear,
  initialSource,
  currentFactorId,
  regions,
  onSelect,
}: FactorSearchModalProps) => {
  const [queryText, setQueryText] = useState(initialQuery);
  const [region, setRegion] = useState<string>(initialRegion ?? '');
  const [year, setYear] = useState<string>(initialYear !== undefined ? String(initialYear) : '');
  const [source, setSource] = useState<string>(initialSource ?? '');
  // Each nullable filter is a (value, enabled) pair. Unchecking the
  // checkbox keeps the value visible but stops the search from sending
  // it — better than zeroing the input on toggle since the user often
  // wants to flip back-and-forth without losing the prefilled value.
  const [regionEnabled, setRegionEnabled] = useState<boolean>(Boolean(initialRegion));
  const [yearEnabled, setYearEnabled] = useState<boolean>(initialYear !== undefined);
  const [sourceEnabled, setSourceEnabled] = useState<boolean>(Boolean(initialSource));
  const [page, setPage] = useState<number>(1);
  const [results, setResults] = useState<AlternativeFactor[]>([]);
  const [pagination, setPagination] = useState<FactorSearchPagination | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [searchSummary, setSearchSummary] = useState<{
    query: string;
    region: string;
    year: string;
    source: string;
  } | null>(null);

  const [search, { isLoading: isSearching }] = useFactorSearchPaginatedMutation();

  const runSearch = async (
    targetPage: number,
    overrides?: {
      query?: string;
      region?: string;
      year?: string;
      source?: string;
      regionEnabled?: boolean;
      yearEnabled?: boolean;
      sourceEnabled?: boolean;
    },
  ) => {
    setSearchError(null);
    const trimmedQuery = (overrides?.query ?? queryText).trim();
    const effRegion = overrides?.region ?? region;
    const effYear = overrides?.year ?? year;
    const effSource = overrides?.source ?? source;
    const effRegionEnabled = overrides?.regionEnabled ?? regionEnabled;
    const effYearEnabled = overrides?.yearEnabled ?? yearEnabled;
    const effSourceEnabled = overrides?.sourceEnabled ?? sourceEnabled;
    const yearNum = effYear.trim() === '' ? undefined : Number(effYear);
    const body: FactorSearchQuery = {
      scope,
      activityType,
      unitType: unitType && unitType.length > 0 ? unitType : undefined,
      query: trimmedQuery || undefined,
      source: effSourceEnabled && effSource.trim() ? effSource.trim() : undefined,
      region: effRegionEnabled && effRegion.trim() ? effRegion.trim() : undefined,
      year:
        effYearEnabled && yearNum !== undefined && Number.isFinite(yearNum) ? yearNum : undefined,
      page: targetPage,
      resultsPerPage: DEFAULT_RESULTS_PER_PAGE,
    };
    setSearchSummary({
      query: trimmedQuery,
      region: effRegionEnabled ? effRegion : '',
      year: effYearEnabled ? effYear : '',
      source: effSourceEnabled ? effSource : '',
    });
    const result = await search({ initiativeId, query: body });
    setHasSearched(true);
    if ('error' in result) {
      setResults([]);
      setPagination(null);
      setSearchError('Search failed. Please adjust your filters and try again.');
      return;
    }
    setResults(result.data.alternatives);
    setPagination(result.data.pagination);
    setPage(result.data.pagination.page);
  };

  // Reset transient state every time the modal is reopened so we don't
  // leak stale results / errors / selections between sessions, and fire
  // an initial search using the prefilled filters so the user lands on
  // results rather than an empty form they have to manually submit.
  useEffect(() => {
    if (!isOpen) return;
    const seedQuery = initialQuery;
    const seedRegion = initialRegion ?? '';
    const seedYear = initialYear !== undefined ? String(initialYear) : '';
    const seedSource = initialSource ?? '';
    const seedRegionEnabled = Boolean(initialRegion);
    const seedYearEnabled = initialYear !== undefined;
    const seedSourceEnabled = Boolean(initialSource);
    setQueryText(seedQuery);
    setRegion(seedRegion);
    setYear(seedYear);
    setSource(seedSource);
    setRegionEnabled(seedRegionEnabled);
    setYearEnabled(seedYearEnabled);
    setSourceEnabled(seedSourceEnabled);
    setPage(1);
    setResults([]);
    setPagination(null);
    setHasSearched(false);
    setSearchError(null);
    setApplyingId(null);
    setApplyError(null);
    setSearchSummary(null);
    void runSearch(1, {
      query: seedQuery,
      region: seedRegion,
      year: seedYear,
      source: seedSource,
      regionEnabled: seedRegionEnabled,
      yearEnabled: seedYearEnabled,
      sourceEnabled: seedSourceEnabled,
    });
    // Intentional: initial* props are read-once when the modal opens.
    // Adding them as deps would reset the user's in-progress filters
    // every time the parent re-renders.
  }, [isOpen]);

  const handleApplyRow = async (factor: AlternativeFactor) => {
    setApplyError(null);
    setApplyingId(factor.id);
    try {
      await onSelect(factor);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply this factor.';
      setApplyError(message);
    } finally {
      setApplyingId(null);
    }
  };

  const summarySentence = useMemo(() => {
    if (!searchSummary) return null;
    // "ANY" stands out so the user immediately sees which dimensions
    // were left unbounded — caps + bold reads as a sentinel value
    // distinct from the surrounding free text.
    const ANY = <span className='tw:font-bold tw:uppercase'>any</span>;
    const queryPhrase = searchSummary.query
      ? <>like <span className='tw:font-medium'>&ldquo;{searchSummary.query}&rdquo;</span></>
      : <>matching the activity</>;
    const regionFragment = (
      <span key='region'>
        in {searchSummary.region
          ? <span className='tw:font-medium'>{formatRegionLabel(searchSummary.region, regions)}</span>
          : <>{ANY} region</>}
      </span>
    );
    const yearFragment = (
      <span key='year'>
        for {searchSummary.year
          ? <>the year <span className='tw:font-medium'>{searchSummary.year}</span></>
          : <>{ANY} year</>}
      </span>
    );
    const sourceFragment = (
      <span key='source'>
        from {searchSummary.source
          ? <span className='tw:font-medium'>{formatSourceLabel(searchSummary.source)}</span>
          : <>{ANY} source</>}
      </span>
    );
    return (
      <>
        Factors {queryPhrase} {regionFragment}, {yearFragment} and {sourceFragment}.
      </>
    );
  }, [searchSummary, regions]);

  const pageNumbers = useMemo(() => buildPageRange(pagination?.page ?? 1, pagination?.lastPage ?? 1), [pagination]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className='tw:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Search emission factors</DialogTitle>
          <DialogDescription>
            Search factor catalogue. To broaden the search, unselect any of the filters.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runSearch(1);
          }}
          className='tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3'
        >
          <div className='tw:col-span-1 tw:sm:col-span-2'>
            <label className='tw:text-xs tw:text-muted-foreground tw:mb-1 tw:block'>
              Query
            </label>
            <input
              type='text'
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder='e.g. natural gas, propane, district heating'
              className='tw:w-full tw:rounded tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm'
              autoFocus
            />
          </div>

          <div>
            <label className='tw:text-xs tw:text-muted-foreground tw:mb-1 tw:block'>
              Unit Type
            </label>
            <input
              type='text'
              disabled
              value={unitType && unitType.length > 0 ? unitType.join(', ') : 'Any'}
              className='tw:w-full tw:rounded tw:border tw:border-border tw:bg-muted tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground'
            />
          </div>

          <div>
            <label className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground tw:mb-1'>
              <input
                type='checkbox'
                checked={regionEnabled}
                onChange={(e) => setRegionEnabled(e.target.checked)}
                className='tw:cursor-pointer'
              />
              Region
            </label>
            {regions && regions.length > 0 ? (
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={!regionEnabled}
                className={cn(
                  'tw:w-full tw:rounded tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm',
                  !regionEnabled && 'tw:bg-muted tw:text-muted-foreground tw:cursor-not-allowed',
                )}
              >
                {regions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type='text'
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={!regionEnabled}
                className={cn(
                  'tw:w-full tw:rounded tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm',
                  !regionEnabled && 'tw:bg-muted tw:text-muted-foreground tw:cursor-not-allowed',
                )}
              />
            )}
          </div>

          <div>
            <label className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground tw:mb-1'>
              <input
                type='checkbox'
                checked={yearEnabled}
                onChange={(e) => setYearEnabled(e.target.checked)}
                className='tw:cursor-pointer'
              />
              Year
            </label>
            <input
              type='number'
              value={year}
              onChange={(e) => setYear(e.target.value)}
              disabled={!yearEnabled}
              min={1990}
              max={2100}
              className={cn(
                'tw:w-full tw:rounded tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm',
                !yearEnabled && 'tw:bg-muted tw:text-muted-foreground tw:cursor-not-allowed',
              )}
            />
          </div>

          <div>
            <label className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground tw:mb-1'>
              <input
                type='checkbox'
                checked={sourceEnabled}
                onChange={(e) => setSourceEnabled(e.target.checked)}
                className='tw:cursor-pointer'
              />
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              disabled={!sourceEnabled}
              className={cn(
                'tw:w-full tw:rounded tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm',
                !sourceEnabled && 'tw:bg-muted tw:text-muted-foreground tw:cursor-not-allowed',
              )}
            >
              {/*
                With the checkbox guarding whether this filter is sent,
                an empty default would silently broaden the search even
                when the user thinks they've picked DEFRA. Default to
                the first option so the dropdown always reflects what
                will actually be queried.
              */}
              {!source && (
                <option value='' disabled hidden>
                  Select a source
                </option>
              )}
              {SOURCE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}{s.premium ? ' — premium' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className='tw:col-span-1 tw:sm:col-span-2 tw:flex tw:justify-end tw:gap-2'>
            <Button type='button' variant='outline' size='sm' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' size='sm' disabled={isSearching}>
              {isSearching ? (
                <>
                  <Spinner size='sm' className='tw:me-1' />
                  Searching...
                </>
              ) : (
                'Search'
              )}
            </Button>
          </div>
        </form>

        {/* Search summary — describes what was just queried so the user
            understands the result set even if they've since edited the
            filters but not re-submitted. Updates only on successful
            submit (not on input change). */}
        {summarySentence && (
          <p className='tw:mt-1 tw:mb-0 tw:text-xs tw:text-muted-foreground'>{summarySentence}</p>
        )}
        {applyError && (
          <p className='tw:text-xs tw:text-destructive tw:mb-0'>{applyError}</p>
        )}

        {/* Results */}
        <div className='tw:mt-2 tw:max-h-96 tw:overflow-y-auto tw:rounded tw:border tw:border-border'>
          {isSearching && results.length === 0 && (
            <div className='tw:py-6 tw:text-center'>
              <Spinner size='sm' color='success' />
              <p className='tw:text-sm tw:text-muted-foreground tw:mt-2 tw:mb-0'>Searching Climatiq…</p>
            </div>
          )}

          {!isSearching && hasSearched && results.length === 0 && !searchError && (
            <p className='tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground tw:mb-0'>
              No factors matched these filters.
            </p>
          )}

          {searchError && (
            <p className='tw:py-6 tw:text-center tw:text-sm tw:text-destructive tw:mb-0'>{searchError}</p>
          )}

          {!searchError && results.length > 0 && (
            <ul className='tw:divide-y tw:divide-border tw:m-0 tw:p-0 tw:list-none'>
              {results.map((alt) => {
                const isCurrent = alt.id === currentFactorId;
                const isApplyingThis = applyingId === alt.id;
                const anyApplying = applyingId !== null;
                return (
                  <li key={alt.id} className='tw:m-0'>
                    <div
                      className={cn(
                        'tw:flex tw:items-center tw:justify-between tw:gap-3 tw:px-3 tw:py-2 tw:text-sm',
                        isCurrent && 'tw:bg-emerald-50',
                      )}
                    >
                      <div className='tw:flex-1 tw:min-w-0'>
                        <div className='tw:flex tw:items-start tw:justify-between tw:gap-2'>
                          <div className='tw:flex-1 tw:font-medium tw:leading-snug'>
                            {alt.name}
                            {isCurrent && (
                              <Badge className='tw:ms-2 tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200 tw:text-xs'>
                                current
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className='tw:flex tw:flex-wrap tw:gap-x-3 tw:gap-y-1 tw:mt-1 tw:text-xs tw:text-muted-foreground'>
                          <span>{alt.source}</span>
                          <span>{alt.region}</span>
                          <span>{alt.year}</span>
                          {alt.factor !== null && (
                            <span>
                              {formatFactorValue(alt.factor)} {alt.unit}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size='xs'
                        variant={isCurrent ? 'outline' : 'default'}
                        disabled={isCurrent || anyApplying}
                        onClick={() => void handleApplyRow(alt)}
                        className='tw:shrink-0'
                      >
                        {isApplyingThis ? (
                          <>
                            <Spinner size='sm' className='tw:me-1' />
                            Applying…
                          </>
                        ) : isCurrent ? (
                          'Applied'
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.lastPage > 1 && (
          <div className='tw:flex tw:items-center tw:justify-between tw:mt-2'>
            <span className='tw:text-xs tw:text-muted-foreground'>
              {pagination.total.toLocaleString()} result{pagination.total === 1 ? '' : 's'}
            </span>
            <div className='tw:flex tw:items-center tw:gap-1'>
              <Button
                variant='outline'
                size='xs'
                onClick={() => void runSearch(Math.max(1, page - 1))}
                disabled={isSearching || page <= 1}
              >
                Prev
              </Button>
              {pageNumbers.map((p, i) =>
                p === '…' ? (
                  <span key={`gap-${i}`} className='tw:px-1 tw:text-xs tw:text-muted-foreground'>
                    …
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size='xs'
                    onClick={() => void runSearch(p)}
                    disabled={isSearching}
                  >
                    {p}
                  </Button>
                ),
              )}
              <Button
                variant='outline'
                size='xs'
                onClick={() => void runSearch(Math.min(pagination.lastPage, page + 1))}
                disabled={isSearching || page >= pagination.lastPage}
              >
                Next
              </Button>
            </div>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
};

/**
 * Compact pagination range like 1 … 4 5 6 … 12 — caps the number of
 * buttons we render even when the user has hundreds of result pages.
 */
const buildPageRange = (current: number, last: number): (number | '…')[] => {
  if (last <= 7) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }
  const range: (number | '…')[] = [1];
  if (current > 4) range.push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(last - 1, current + 1);
  for (let i = start; i <= end; i++) range.push(i);
  if (current < last - 3) range.push('…');
  range.push(last);
  return range;
};
